import type { InitOptions } from 'i18next';
import { resolveLocale, buildI18nextConfig } from '@withjoy/locale';
import { SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE, GUEST_LANGUAGE_COOKIE_NAME } from './i18n.constants';

export { LANGUAGES, DEFAULT_LANGUAGE } from './i18n.constants';

// Static imports to avoid webpack warning and bundle bloat.
//
// Keyed by the BASE language code (`'es'`, not `'es-ES'`) because the consumer
// hook `useReactDatePickersLocale` indexes this map with `locale.language.split('-')[0]`.
// `date-fns/locale/es` is itself a base-language module — region-specific date-fns
// variants exist but are not loaded here; the base locale is a good default for
// every region of a language.
export const reactDatepickerLocaleImports: Record<string, () => Promise<{ default: Locale }>> = {
  es: () => import('date-fns/locale/es'),
  de: () => import('date-fns/locale/de'),
  fr: () => import('date-fns/locale/fr'),
  it: () => import('date-fns/locale/it'),
  ja: () => import('date-fns/locale/ja'),
};

export const getInitOptions = (overrides: Readonly<Partial<InitOptions>> = {}): InitOptions => {
  const base = buildI18nextConfig({
    supportedLocales: SUPPORTED_LANGUAGE_CODES,
    fallbackLocale: DEFAULT_LANGUAGE,
    // i18next's LanguageDetector defaults to lookupQuerystring='lng', which
    // collides with the hotel/accommodations search 'lng' longitude URL
    // param. Use 'l' (already recognized in LANGUAGE_PARAMS) instead. See
    // JW-47.
    lookupQuerystring: 'l',
  });

  return {
    ...base,
    // Server-side LanguageDetector order is querystring > cookie > header; pointing
    // lookupCookie at the guest-picker cookie makes SSR render the guest's saved
    // language on first paint. Merged (not replaced) to keep convertDetectedLanguage.
    detection: { ...(base.detection as object), lookupCookie: GUEST_LANGUAGE_COOKIE_NAME },

    // joy-web-specific overrides — not universal, kept out of the shared
    // package. Fields now locked in buildI18nextConfig and dropped from
    // here: load, nonExplicitSupportedLngs, cleanCode, interpolation,
    // fallbackLng, supportedLngs, and the querystring detection block.
    preload: [DEFAULT_LANGUAGE],
    returnEmptyString: false,
    partialBundledLanguages: true,

    react: {
      useSuspense: true,
      // wait=true in browsers, false on Node/SSR. The typeof guard (not just
      // `process?.`) matters post-P6: without the webpack process shim, a bare
      // `process` in the browser is an undeclared identifier (ReferenceError).
      // Truth table preserved from the shim era: shim had no `release` → true
      // in browsers; real Node has `release` → false on the server.
      wait: typeof process === 'undefined' || !process.release,
    },
    postProcess: [`reactPostprocessor`],
    ...overrides,
  };
};

type ParsedFormat = {
  dateFormat: string[];
  dateInputMask: string[];
};

export const getDateFormatString = (locale: string) => {
  const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());
  const parsed = formatObj.reduce(
    (acc, obj) => {
      switch (obj.type) {
        case 'day':
          acc.dateFormat.push('dd');
          acc.dateInputMask.push('99');
          break;
        case 'month':
          acc.dateFormat.push('MM');
          acc.dateInputMask.push('99');
          break;
        case 'year':
          acc.dateFormat.push('yyyy');
          acc.dateInputMask.push('9999');
          break;
      }
      return acc;
    },
    { dateFormat: [], dateInputMask: [] } as ParsedFormat,
  );
  return {
    dateFormat: parsed.dateFormat.join('/'),
    dateInputMask: parsed.dateInputMask.join('/'),
  };
};

/**
 * Resolves an arbitrary BCP 47 language code against joy-web's supported set.
 *
 * Returned `ResolvedLocale.locale` is the canonical bundle key i18next actually
 * loads (e.g. `fr-CA` → `fr-FR`, `de-AT` → `de-DE`, bare `de` → `de-DE`).
 * `.recognized` is `false` only when the input's base language has no
 * counterpart in `SUPPORTED_LANGUAGE_CODES` and would fall through to
 * `DEFAULT_LANGUAGE`.
 *
 * Use this whenever calling `i18n.changeLanguage()` from application code:
 * `changeLanguage` bypasses i18next's detection pipeline (and therefore the
 * `convertDetectedLanguage` closure inside `getInitOptions`), so raw regional
 * codes must be canonicalized here or i18next will fail to find a bundle and
 * fall back to English. See PR #15943 codex review round 1.
 */
export const resolveSupportedLocale = (extendedLanguageCode: string) => resolveLocale(extendedLanguageCode, SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE);

/**
 * Whether a given BCP 47 language code is accepted by joy-web's i18n.
 *
 * Returns `true` when the input resolves to a supported locale by any means —
 * exact match, case-normalized match, closest sibling (e.g. de-AT → de-DE),
 * or base-language match (bare `de` → de-DE). Returns `false` only when the
 * input's base language has no counterpart in `SUPPORTED_LANGUAGE_CODES` and
 * would fall through to the default fallback.
 *
 * Liberal interpretation per PRNT-4468 — telemetry / i18n.language preserves
 * the user's reported region (e.g. 'en-CA'); content resolution collapses to
 * the canonical sibling. For that canonical form, use `resolveSupportedLocale`.
 */
export const languageSupported = (extendedLanguageCode: string): boolean => resolveSupportedLocale(extendedLanguageCode).recognized;
